import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TEXTO_GRADIENTE = {
  background: 'linear-gradient(135deg, #831843 0%, #EC4899 55%, #A855F7 100%)',
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
};

const SERIF = { fontFamily: "'Playfair Display', serif" };

// Onda divisoria entre secciones
function Onda({ flip = false }) {
  return (
    <div aria-hidden style={{ lineHeight: 0, transform: flip ? 'rotate(180deg)' : 'none' }}>
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '48px' }}>
        <path d="M0,40 C240,90 480,0 720,30 C960,60 1200,10 1440,40 L1440,80 L0,80 Z" fill="rgba(236,72,153,0.07)" />
        <path d="M0,55 C300,15 600,75 900,45 C1140,20 1320,55 1440,45 L1440,80 L0,80 Z" fill="rgba(168,85,247,0.06)" />
      </svg>
    </div>
  );
}

const PROGRAMAS = [
  {
    tipo: 'militar',
    titulo: 'Inglés Militar',
    subtitulo: 'Programa SLP',
    descripcion: 'Preparación específica para los exámenes SLP (STANAG) y pruebas de acceso y curso de suboficiales y oficiales.',
    color: '#EC4899',
    niveles: ['SLP2', 'SLP3', 'SLP4', 'Acceso Suboficial', 'Acceso Oficial', 'Curso Suboficial', 'Curso Oficial'],
  },
  {
    tipo: 'civil',
    titulo: 'Inglés Civil',
    subtitulo: 'Cambridge',
    descripcion: 'Cursos orientados a las certificaciones oficiales de Cambridge desde el nivel B1 hasta el C2.',
    color: '#A855F7',
    niveles: ['B1', 'B2', 'C1', 'C2'],
  },
];

const VENTAJAS = [
  { titulo: 'Clases en directo', texto: 'Sesiones online con profesores especializados.' },
  { titulo: 'Material propio', texto: 'Recursos y exámenes diseñados por la academia.' },
  { titulo: 'Seguimiento', texto: 'Acompañamiento personalizado hasta tu objetivo.' },
];

const STATS = [
  { num: '+500', label: 'Alumnos formados' },
  { num: '95%', label: 'Aprobados en SLP' },
  { num: '+10', label: 'Años de experiencia' },
  { num: '100%', label: 'Clases en directo' },
];

const TESTIMONIOS = [
  { cita: 'Aprobé el SLP2 a la primera. Las clases en directo y el material personalizado son estupendos.', nombre: 'María G.', logro: 'Suboficial · SLP2' },
  { cita: 'Pasé de un B1 a un B2 de Cambridge en menos de un año. La profesora es exigente, lo que te ayuda a mejorar.', nombre: 'Carlos R.', logro: 'Cambridge B2' },
  { cita: 'Gracias al seguimiento personalizado me ayudaron a prepararme específicamente lo que quería.', nombre: 'Laura M.', logro: 'Acceso Oficial' },
];

const SELLOS = ['Cambridge English', 'STANAG / OTAN'];

const glass = {
  background: 'rgba(255,255,255,0.55)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow: '0 12px 40px rgba(236,72,153,0.18)',
};

function Encabezado({ kicker, titulo }) {
  return (
    <div className="reveal" style={{ textAlign: 'center', marginBottom: '36px' }}>
      <p style={{
        color: '#DB2777', fontSize: '13px', fontWeight: 700, letterSpacing: '3px',
        textTransform: 'uppercase', margin: '0 0 8px',
      }}>
        {kicker}
      </p>
      <h2 style={{ ...SERIF, color: '#581C47', fontSize: 'clamp(28px, 4.2vw, 36px)', fontWeight: 800, margin: 0 }}>
        {titulo}
      </h2>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();

  // Scroll reveal: revela elementos .reveal al entrar en viewport
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('reveal-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const botonCampus = (extra = {}) => (
    <button onClick={() => navigate('/login')} style={{
      padding: '16px 42px', borderRadius: '50px',
      background: 'linear-gradient(135deg, #EC4899 0%, #A855F7 100%)',
      border: 'none', color: '#fff', fontSize: '17px', fontWeight: 700,
      cursor: 'pointer', boxShadow: '0 10px 30px rgba(168,85,247,0.45)',
      transition: 'transform 0.2s, box-shadow 0.2s', ...extra,
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 38px rgba(168,85,247,0.55)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(168,85,247,0.45)'; }}
    >
      Acceder al Campus Virtual →
    </button>
  );

  return (
    <div style={{
      minHeight: '100vh', position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(160deg, #FDF2F8 0%, #FCE7F3 45%, #F3E8FF 100%)',
    }}>
      <style>{`
        @keyframes blobFloat {
          0%   { transform: translate(0,0) scale(1); }
          33%  { transform: translate(40px,-30px) scale(1.1); }
          66%  { transform: translate(-30px,20px) scale(0.95); }
          100% { transform: translate(0,0) scale(1); }
        }
        @keyframes riseIn {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .blob { position: absolute; border-radius: 50%; filter: blur(60px); pointer-events: none; }
        .rise { opacity: 0; animation: riseIn 0.7s ease forwards; }
        .reveal { opacity: 0; transform: translateY(34px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .reveal-in { opacity: 1; transform: none; }
        @media (max-width: 600px) {
          .logo-extra { display: none !important; }
        }
      `}</style>

      {/* Blobs flotantes */}
      <div className="blob" style={{ width: 420, height: 420, top: '-80px', left: '-60px', background: 'rgba(236,72,153,0.45)', animation: 'blobFloat 14s ease-in-out infinite' }} />
      <div className="blob" style={{ width: 360, height: 360, top: '20%', right: '-80px', background: 'rgba(168,85,247,0.40)', animation: 'blobFloat 18s ease-in-out infinite reverse' }} />
      <div className="blob" style={{ width: 300, height: 300, bottom: '-60px', left: '25%', background: 'rgba(249,168,212,0.55)', animation: 'blobFloat 16s ease-in-out infinite' }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: '1040px', margin: '0 auto', padding: '0 24px' }}>
        {/* ── HERO ───────────────────────────────── */}
        <section style={{
          textAlign: 'center', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: '18px', padding: '40px 0 56px',
        }}>
          <p className="rise" style={{
            color: '#DB2777', fontSize: '14px', fontWeight: 700, letterSpacing: '4px',
            textTransform: 'uppercase', margin: 0,
          }}>
            Welcome · Bienvenido
          </p>

          {/* Logo + título + logo (simétrico; en móvil solo el izquierdo) */}
          <div className="rise" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 'clamp(14px, 2.5vw, 28px)', flexWrap: 'nowrap', animationDelay: '0.06s',
          }}>
            {[0, 1].map(i => (
              <div key={i} className={i === 1 ? 'logo-extra' : undefined} style={{ position: 'relative', display: 'inline-block', flexShrink: 0, order: i === 0 ? 0 : 2 }}>
                <div style={{
                  position: 'absolute', inset: '-16%', borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(236,72,153,0.40) 0%, rgba(168,85,247,0.22) 45%, transparent 70%)',
                  filter: 'blur(26px)', zIndex: 0,
                }} />
                <img src="/logo-sa.png" alt="Su Academia" style={{
                  position: 'relative', zIndex: 1,
                  width: 'clamp(54px, 7vw, 78px)', height: 'auto',
                  borderRadius: '50%',
                  boxShadow: '0 12px 30px rgba(168,85,247,0.28)',
                  border: '3px solid rgba(255,255,255,0.65)',
                }} />
              </div>
            ))}

            <h1 style={{
              order: 1,
              fontSize: 'clamp(48px, 8.5vw, 84px)', fontWeight: 800, lineHeight: 1.02,
              margin: 0, ...TEXTO_GRADIENTE,
            }}>
              Su Academia
            </h1>
          </div>

          <p className="rise" style={{
            color: '#6B2D54', fontSize: 'clamp(17px, 2.4vw, 21px)', lineHeight: 1.55, maxWidth: '640px',
            margin: 0, animationDelay: '0.12s', fontWeight: 500,
          }}>
            Domina el inglés que tu carrera necesita. Te preparamos tu inglés
            militar o civil.
          </p>

          <div className="rise" style={{ animationDelay: '0.2s', marginTop: '8px' }}>
            {botonCampus()}
          </div>

          {/* Ventajas — solo texto grande, sin emojis */}
          <div className="rise" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '18px', marginTop: '40px', width: '100%', animationDelay: '0.28s',
          }}>
            {VENTAJAS.map(v => (
              <div key={v.titulo} style={{ ...glass, borderRadius: '22px', padding: '34px 26px' }}>
                <p style={{ ...SERIF, fontSize: '24px', fontWeight: 800, margin: '0 0 10px', lineHeight: 1.2, ...TEXTO_GRADIENTE }}>
                  {v.titulo}
                </p>
                <p style={{ color: '#6B2D54', fontSize: '15px', lineHeight: 1.55, margin: 0 }}>{v.texto}</p>
              </div>
            ))}
          </div>
        </section>

        <Onda />

        {/* ── CIFRAS ─────────────────────────────── */}
        <section style={{ paddingBottom: '56px' }}>
          <div className="reveal" style={{
            ...glass, borderRadius: '20px', padding: 'clamp(18px, 3vw, 26px)',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px',
          }}>
            {STATS.map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <p style={{ ...SERIF, fontSize: 'clamp(26px, 4.5vw, 34px)', fontWeight: 800, margin: '0 0 2px', lineHeight: 1, ...TEXTO_GRADIENTE }}>
                  {s.num}
                </p>
                <p style={{ color: '#6B2D54', fontSize: '12.5px', fontWeight: 600, margin: 0 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <Onda flip />

        {/* ── PROGRAMAS + NIVELES ────────────────── */}
        <section style={{ paddingBottom: '64px' }}>
          <Encabezado kicker="Our programs" titulo="Nuestros programas" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '22px' }}>
            {PROGRAMAS.map((p, i) => (
              <div key={p.tipo} className="reveal" style={{
                ...glass, borderRadius: '24px', padding: '40px 32px', textAlign: 'center',
                transitionDelay: `${i * 0.08}s`, position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: -40, right: -40, width: 160, height: 160,
                  borderRadius: '50%', background: `${p.color}22`, filter: 'blur(24px)',
                }} />
                <h3 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 6px', lineHeight: 1.15, ...TEXTO_GRADIENTE }}>{p.titulo}</h3>
                <p style={{ color: p.color, fontSize: '13px', fontWeight: 700, margin: '0 0 18px', letterSpacing: '1px', textTransform: 'uppercase' }}>{p.subtitulo}</p>
                <p style={{ color: '#6B2D54', fontSize: '15px', lineHeight: 1.6, margin: '0 0 20px' }}>{p.descripcion}</p>

                {/* Badges de niveles */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                  {p.niveles.map(n => (
                    <span key={n} style={{
                      fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '50px',
                      color: p.color, background: `${p.color}14`, border: `1px solid ${p.color}33`,
                    }}>
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <Onda />

        {/* ── TESTIMONIOS ────────────────────────── */}
        <section style={{ paddingBottom: '56px' }}>
          <Encabezado kicker="What our students say" titulo="Lo que dicen nuestros alumnos" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            {TESTIMONIOS.map((t, i) => (
              <div key={t.nombre} className="reveal" style={{
                ...glass, borderRadius: '18px', padding: '20px 20px', textAlign: 'left',
                transitionDelay: `${i * 0.08}s`, display: 'flex', flexDirection: 'column',
              }}>
                <p style={{ color: '#52344A', fontSize: '13.5px', lineHeight: 1.55, margin: '0 0 14px', flex: 1, fontStyle: 'italic' }}>
                  “{t.cita}”
                </p>
                <p style={{ color: '#581C47', fontSize: '14px', fontWeight: 800, margin: 0 }}>{t.nombre}</p>
                <p style={{ color: '#DB2777', fontSize: '11px', fontWeight: 700, margin: '2px 0 0', letterSpacing: '0.5px' }}>{t.logro}</p>
              </div>
            ))}
          </div>
        </section>

        <Onda flip />

        {/* ── SELLOS / CERTIFICACIONES ───────────── */}
        <section style={{ paddingBottom: '72px', textAlign: 'center' }}>
          <Encabezado kicker="Certifications" titulo="Preparamos certificaciones oficiales" />
          <div className="reveal" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center', marginBottom: '40px' }}>
            {SELLOS.map(s => (
              <span key={s} style={{
                ...glass, borderRadius: '50px', padding: '14px 30px',
                color: '#581C47', fontSize: '16px', fontWeight: 800, letterSpacing: '0.5px',
              }}>
                {s}
              </span>
            ))}
          </div>
          <div className="reveal">
            {botonCampus()}
          </div>
        </section>
      </div>

      {/* ── FOOTER ─────────────────────────────── */}
      <footer style={{
        position: 'relative', zIndex: 1, ...glass, borderRadius: 0,
        borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
        padding: '30px 24px', textAlign: 'center',
      }}>
        <p style={{ color: '#581C47', fontSize: '15px', fontWeight: 800, margin: '0 0 6px' }}>
          Su Academia · Campus Virtual
        </p>
        <p style={{ color: '#7A5068', fontSize: '13px', margin: 0 }}>
          ¿Dudas?{' '}
          <a href="mailto:academiasu9@gmail.com" style={{ color: '#DB2777', textDecoration: 'underline', fontWeight: 600 }}>
            academiasu9@gmail.com
          </a>
        </p>
      </footer>
    </div>
  );
}
